import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import { formHasField } from 'features/arretTravail/models/scenarii';
import { RepriseOptions } from 'features/arretTravail/models/StopInformation/reprise';
import { TrueFalse } from 'features/arretTravail/models/StopInformation/workStoppage';
import { isValidDate } from 'features/arretTravail/utils/tools';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

export const useWorkStoppageSchema = ({ store, scenario }) => {
    const { t } = useTranslation();
    const requiredMessage = t('yup.validators.required');
    const { hasWorkStoppageCotisAmount } = useArretTravailFormContext();

    return Yup.object().shape({
        infos: Yup.object().shape({
            start: Yup.date().nullable().required(requiredMessage),
            end: Yup.date()
                .nullable()
                .required(requiredMessage)
                .test('end-greater_or_egale-than-start', function (value) {
                    const { path, parent, createError } = this;
                    const { start } = parent;

                    if (!value || !start || !isValidDate(start)) return true;
                    if (value >= start) return true;

                    return createError({
                        path,
                        message: `${t('workStop.validation.workStoppage.infos.endGreaterorEgalThanStart')} ${moment(start).format('DD/MM/YYYY')}`,
                    });
                }),
            lastDayWorked: Yup.date()
                .nullable()
                .required(requiredMessage)
                .test('lastDayWorked-lower-or-equal-than-start', function (value) {
                    const { path, parent, createError } = this;
                    const { start } = parent;

                    if (!value || !start || !isValidDate(start)) return true;
                    if (value <= start) return true;

                    return createError({
                        path,
                        message: `${t('workStop.validation.workStoppage.infos.lastDayWorkedLowerOrEqualThanStart')} ${moment(start).format('DD/MM/YYYY')}`,
                    });
                })
                .test('lastDayWorked-lower-or-equal-than-today', function (value) {
                    const { path, parent, createError } = this;
                    const { start } = parent;

                    if (!value || !start || !isValidDate(start)) return true;
                    if (value <= new Date()) return true;

                    return createError({
                        path,
                        message: `${t('workStop.validation.workStoppage.infos.lastDayWorkedLowerOrEqualThanToday')}`,
                    });
                }),
            isHospitalized: Yup.string().required(requiredMessage),
            hospitalizedDate: Yup.date()
                .nullable()
                .when('isHospitalized', {
                    is: TrueFalse.True,
                    then: Yup.date().required(requiredMessage),
                    otherwise: Yup.date().nullable(),
                }),
            parentalLeaveStartDate: formHasField(['workStoppage.infos.parentalLeaveStartDate'], 'formAtFields', store) ? Yup.date().required(requiredMessage) : Yup.date().nullable(),
            childDateOfBirth: formHasField(['workStoppage.infos.childDateOfBirth'], 'formAtFields', store) ? Yup.date().required(requiredMessage) : Yup.date().nullable(),
            firstConstatationDate: formHasField(['workStoppage.infos.firstConstatationDate'], 'formAtFields', store) ? Yup.date().required(requiredMessage) : Yup.date().nullable(),
            returnToWorkCod: !scenario?.displayLegendRightsCase ? Yup.string().required(requiredMessage) : Yup.string().nullable(),
            returnToWorkDate: !scenario?.displayLegendRightsCase
                ? Yup.date()
                      .nullable()
                      .when('returnToWorkCod', {
                          is: (value) => value !== RepriseOptions.Non,
                          then: Yup.date().nullable().required(requiredMessage),
                          otherwise: Yup.date().nullable(),
                      })
                      .test('returnToWorkDate-greater-or-equal-than-lastDayWorked', function (value) {
                          const { path, parent, createError } = this;
                          const { lastDayWorked } = parent;

                          if (!value || !lastDayWorked || !isValidDate(lastDayWorked)) return true;
                          if (value > lastDayWorked) return true;

                          return createError({
                              path,
                              message: `${t('workStop.validation.workStoppage.infos.returnToWorkDateGreaterThanlastDayWorked')} ${moment(lastDayWorked).format('DD/MM/YYYY')}`,
                          });
                      })
                : Yup.date().nullable(),
            accidentDate: formHasField(['workStoppage.infos.accidentDate'], 'formAtFields', store)
                ? Yup.date()
                      .nullable()
                      .required(requiredMessage)
                      .test('returnToWorkDate-greater-or-equal-than-lastDayWorked', function (value) {
                          const { path, parent, createError } = this;
                          const { lastDayWorked } = parent;

                          if (!value || !lastDayWorked || !isValidDate(lastDayWorked)) return true;
                          if (value <= lastDayWorked) return true;

                          return createError({
                              path,
                              message: `${t('workStop.validation.workStoppage.infos.accidentDateLowerOrEqualThanlastDayWorked')} ${moment(lastDayWorked).format('DD/MM/YYYY')}`,
                          });
                      })
                : Yup.date().nullable(),
            hasOtherVictims: formHasField(['workStoppage.infos.hasOtherVictims'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string().nullable(),
            situationAtStopDateCod: formHasField(['workStoppage.infos.situationAtStopDateCod'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string().nullable(),
        }),
        rights: Yup.object().shape({
            workedHours: formHasField(['workStoppage.rights.workedHours'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
            period: formHasField(['workStoppage.rights.period'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
            start: formHasField(['workStoppage.rights.start'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
            end: formHasField(['workStoppage.rights.end'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
            contribution: formHasField(['workStoppage.rights.contribution'], 'formAtFields', store) && hasWorkStoppageCotisAmount ? Yup.string().required(requiredMessage) : Yup.string().nullable(),
        }),
        cotisation: Yup.object().shape({
            p12m: formHasField(['workStoppage.cotisation.p12m'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
            p6m: formHasField(['workStoppage.cotisation.p6m'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
        }),
        hours: Yup.object().shape({
            cases800: formHasField(['workStoppage.hours.cases800'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
            cases200: formHasField(['workStoppage.hours.cases200'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string(),
        }),
        rightsS3202: Yup.object().shape({
            workedHours1: formHasField(['workStoppage.rightsS3202.workedHours1'], 'formAtFields', store) ? Yup.string().required(requiredMessage) : Yup.string().nullable(),
            workedHours2: Yup.string().nullable(),
            contribution1: Yup.number().nullable().min(0, 'Le montant de cotisation doit être supérieur ou égal à zéro'),
            contribution2: Yup.number().nullable().min(0, 'Le montant de cotisation doit être supérieur ou égal à zéro'),
        }),
    });
};
